import React from 'react';
import { linkifyText, Spacer } from 'src/common';
import { Label, Wrap } from './tableSheets.styles';
import FormulaPreview from 'src/components/FormulaPreview';
import { useFrameworkControlCostFields } from 'src/components/ControlCost';

// Structural type: only the fields the sheet actually renders. Callers pass rows from
// several different queries (and one REST shape), all supersets of this.
export interface ControlLibrarySheetData {
  name?: string | null;
  shortname?: string | null;
  control_identifier?: string | null;
  strategy?: string | null;
  description?: string | null;
  annualCostFormula?: string | null;
  implementationCostFormula?: string | null;
  costNote?: string | null;
  controlFramework?: {
    shortname?: string | null;
    author?: string | null;
    description?: string | null;
  } | null;
}

interface TableSheetControlLibraryProps {
  data: ControlLibrarySheetData;
  /**
   * CRPD-7267 — show the cost fields even when the control carries none, with the usual "–"
   * placeholder.
   *
   * OFF by default, and that default is load-bearing: CRPD-7345 and CRPD-7287 (Phase 13)
   * deliberately HIDE an empty cost block on workspace surfaces, and this component has seven
   * render sites. Only the content editor opts in, because it is the authoring surface — the
   * complaint that started this was "when I'm uploading some new content, I couldn't actually see
   * the annual cost stuff anywhere", and a field you cannot see is a field you cannot tell is
   * empty.
   */
  alwaysShowCost?: boolean;
  /**
   * CRPD-7267 (B1) — the content editor's authoring view: show the control's OWN description, never
   * the framework's in its place.
   *
   * OFF by default, because the six workspace surfaces use the substitution as context. It matters
   * here because this substitution is what HID the reported bug: NIST CSF (v1) has 0 of 108 control
   * descriptions, so its export was correctly blank while this sheet showed the framework blurb
   * under a "DESCRIPTION" label — and the platform looked like it held content it did not.
   */
  authoringView?: boolean;
}

export const TableSheetControlLibrary: React.FC<TableSheetControlLibraryProps> = ({
  data,
  alwaysShowCost = false,
  authoringView = false,
}) => {
  const costFields = useFrameworkControlCostFields(data);
  const description = authoringView ? data.description : data.description || data.controlFramework?.description;
  const hasAnyCost = !!(costFields?.annualCostFormula || costFields?.implementationCostFormula || costFields?.costNote);

  return (
    <Wrap>
      <div className="grid grid--2">
        <div>
          <Label>NAME</Label>
          <div className="value" data-cy="sheet-name">
            {data.name}
          </div>
        </div>
        <div>
          <Label>Short Name</Label>
          <div className="value">{data.shortname}</div>
        </div>
      </div>
      <Spacer $px={10} />
      <div>
        <Label>Framework</Label>
        <div className="value">{data.controlFramework?.shortname}</div>
      </div>

      <Spacer $px={10} />
      <div className="grid grid--3">
        <div>
          <Label>Framework Control ID</Label>
          <div className="value">{data.control_identifier}</div>
        </div>
        <div>
          <Label>Strategy</Label>
          <div className="value">{data.strategy}</div>
        </div>
        <div>
          <Label>Author</Label>
          <div className="value">{data.controlFramework?.author}</div>
        </div>
      </div>
      <Spacer $px={10} />
      <Label>DESCRIPTION</Label>
      <div className="value">{description ? linkifyText(description) : null}</div>
      {(hasAnyCost || alwaysShowCost) && (
        <>
          <Spacer $px={10} />
          <div className="grid grid--2">
            <div>
              <Label>Annual Cost</Label>
              <div className="value">
                {costFields?.annualCostFormula ? <FormulaPreview value={costFields.annualCostFormula} /> : '-'}
              </div>
            </div>
            <div>
              <Label>Implementation Cost</Label>
              <div className="value">
                {costFields?.implementationCostFormula ? (
                  <FormulaPreview value={costFields.implementationCostFormula} />
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
          {(costFields?.costNote || alwaysShowCost) && (
            <>
              <Spacer $px={10} />
              <Label>Cost Note</Label>
              <div className="value">{costFields?.costNote || '-'}</div>
            </>
          )}
        </>
      )}
    </Wrap>
  );
};
